import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { XYChart } from '@amcharts/amcharts5/xy';
import { ChartViewOptions } from '../components/slices/AssetTrendDataSlice';
import {
  createCursorBullet,
  createTooltip,
  customizeAxisLabels,
  DowntimeEntry,
  HighlightEvent,
  renderDowntimeAndHighlightEvents,
  setupCursor,
  prepareChartContainer,
} from './chartHelpers';

interface DataPoint {
  date: number;
  value: number;
  index: number;
}

export interface ChartConfig {
  chartColor: string;
  lineType: string;
  lineWeight: number;
  markerColor: string;
  markerType: string;
  markerSize: number;
}

interface ChartCallbacks {
  onHover: (value: number, date: Date, dataPointIndex: number) => void;
  onMouseLeave: () => void;
}

export interface ChartProps {
  chart: any;
  root: am5.Root;
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>;
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;
  data: DataPoint[];
  maxThresholdValues: DataPoint[] | [];
  minThresholdValues: DataPoint[] | [];
  callbacks: ChartCallbacks;
  chartColor: string;
  markerColor: string;
  markerType: string;
  lineType: string;
  lineWeight: number;
  markerSize: number;
  title: string;
  shortUnitOfMeasure: string;
  syncCursor: () => void;
  showTooltip: boolean;
  showSD: boolean | null;
}

const processThresholdValues = (thresholdValues: DataPoint[]) => {
  return thresholdValues.reduce((acc: DataPoint[], point, index) => {
    // Skip the first and last points
    if (index === 0) {
      acc.push(point); // Add the first and last points directly
      return acc;
    }

    const prevPoint = thresholdValues[index - 1];
    if (prevPoint.value !== point.value) {
      // Add an intermediate point to create a vertical line
      acc.push({
        date: point.date, // Same date as the current point
        value: prevPoint.value, // Keep the previous value
        index: point.index,
      });
    }

    acc.push(point); // Add the current point
    return acc;
  }, []);
};

// Largest-Triangle-Three-Buckets downsampling for performance on large ranges
// Keeps visual shape while capping the number of points rendered
function lttb(data: DataPoint[], threshold: number): DataPoint[] {
  const n = data.length;
  if (threshold >= n || threshold <= 0) return data;
  const sampled: DataPoint[] = [];
  // Bucket size. Leave room for first and last point
  const every = (n - 2) / (threshold - 2);
  let a = 0; // Initially a is the first point
  sampled.push(data[a]);

  for (let i = 0; i < threshold - 2; i++) {
    const avgStart = Math.floor((i + 1) * every) + 1;
    const avgEnd = Math.floor((i + 2) * every) + 1;
    const avgRangeEnd = avgEnd < n ? avgEnd : n;
    let avgX = 0;
    let avgY = 0;
    const avgRangeLength = avgRangeEnd - avgStart;
    for (let j = avgStart; j < avgRangeEnd; j++) {
      avgX += data[j].date;
      avgY += data[j].value;
    }
    avgX /= Math.max(1, avgRangeLength);
    avgY /= Math.max(1, avgRangeLength);

    const rangeOffs = Math.floor(i * every) + 1;
    const rangeTo = Math.floor((i + 1) * every) + 1;
    const rangeToClamped = rangeTo < n ? rangeTo : n - 1;

    let maxArea = -1;
    let maxAreaPoint = data[rangeOffs];
    for (let j = rangeOffs; j < rangeToClamped; j++) {
      const ax = data[a].date;
      const ay = data[a].value;
      const bx = data[j].date;
      const by = data[j].value;
      // Triangle area without division by 2
      const area = Math.abs((ax - avgX) * (by - ay) - (ax - bx) * (avgY - ay));
      if (area > maxArea) {
        maxArea = area;
        maxAreaPoint = data[j];
      }
    }
    sampled.push(maxAreaPoint);
    a = data.indexOf(maxAreaPoint);
  }

  sampled.push(data[n - 1]);
  return sampled;
}

function chooseMaxPointsForContainer(container: HTMLElement, chartType: string | undefined): number {
  const width = Math.max(600, container.clientWidth || 0);
  // Rough rule: up to 2 points per pixel for line, 1 per pixel for scatter, bounded
  const perPx = chartType === 'scatter' ? 1 : 2;
  return Math.max(800, Math.min(8000, Math.floor(width * perPx)));
}

const buildCursor = (
  chart: any,
  root: am5.Root,
  series: any,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  markerColor: string,
  callbacks: ChartCallbacks,
  syncCursor: () => void,
) => {
  // Add cursor for better UX
  const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2, yAxis);

  const cursorBullet = createCursorBullet(root, markerColor);
  series.bulletsContainer.children.push(cursorBullet.get('sprite'));

  // Update the bullet position on cursor movement
  cursor.events.on('cursormoved', function () {
    const cursorPositionX: any = cursor.getPrivate('positionX');
    const cursorPositionY: any = cursor.getPrivate('positionY');
    const xValue = xAxis.positionToValue(cursorPositionX);
    const yValue = yAxis.positionToValue(cursorPositionY);

    //const cursorPositionY: any = cursor.getPrivate('positionY');

    const xPosition = xAxis.valueToPosition(xValue);
    const yPosition = yAxis.valueToPosition(yValue);

    const xCoord = chart.plotContainer.width() * xPosition;
    const yCoord = chart.plotContainer.height() * yPosition; // Invert y-axis position

    // Update the bullet position
    cursorBullet.get('sprite')?.setAll({
      x: xCoord,
      y: yCoord,
      visible: true,
    });

    syncCursor();
  });
  // Hide the bullet when the cursor leaves the chart
  cursor.events.on('cursorhidden', function () {
    cursorBullet.get('sprite')?.set('visible', false);
  });

  // Handle mouse events on the chart
  chart.plotContainer.events.on('pointerover', function () {
    // Show the cursor when mouse enters
    cursor.show();
  });

  chart.plotContainer.events.on('pointerout', function () {
    // Hide cursor when mouse leaves
    cursor.hide();

    callbacks.onMouseLeave();
  });

  return cursor;
};

export const generateLineType = (series: any, lineType: string, lineWeight: number) => {
  lineWeight = Number(lineWeight);
  if (lineType === 'solid-3') {
    series.strokes.template.setAll({
      strokeWidth: 2 + lineWeight,
      strokeHeight: 3,
      strokeDasharray: [],
    });
  } else if (lineType === 'solid-2') {
    series.strokes.template.setAll({
      strokeWidth: 1 + lineWeight,
      strokeHeight: 2,
      strokeDasharray: [],
    });
  } else if (lineType === 'solid') {
    series.strokes.template.setAll({
      strokeWidth: lineWeight,
      strokeDasharray: [],
    });
  } else if (lineType === 'dashed-1') {
    series.strokes.template.setAll({
      strokeWidth: 1 + lineWeight,

      strokeDasharray: [2, 2],
    });
  } else if (lineType === 'dashed-2') {
    series.strokes.template.setAll({
      strokeWidth: 2 + lineWeight,
      strokeDasharray: [2, 2],
    });
  } else if (lineType === 'dashed-3') {
    series.strokes.template.setAll({
      strokeWidth: 3 + lineWeight,
      strokeDasharray: [2, 2],
    });
  }
};
const generateTimeSeries = (seriesChartProps: ChartProps) => {
  const {
    chart,
    root,
    xAxis,
    yAxis,
    data,
    maxThresholdValues,
    minThresholdValues,
    callbacks,
    chartColor,
    lineType,
    lineWeight,
    title,
    shortUnitOfMeasure,
    syncCursor,
    showTooltip,
    showSD,
  } = seriesChartProps;

  // Downsample data for performance on large ranges
  const maxPoints = chooseMaxPointsForContainer(chart.root.dom, 'line');
  const sampledData = data.length > maxPoints ? lttb(data, maxPoints) : data;

  const meanThresholdValue = ((minThresholdValues[0]?.value ?? 0) + (maxThresholdValues[0]?.value ?? 0)) / 2;
  const tooltip = createTooltip(
    `${title} {valueY} ${shortUnitOfMeasure} {valueX.formatDate("MMM d, yyyy hh:mm a")}`,
    root,
    {
      backgroundColor: '#001023',
      backgroundOpacity: 0.6,
      strokeColor: '#4A5463',
      textColor: '#FFFFFF',
      fontSize: 14,
      verticalPosition: 'top',
      thresholds: {
        setpoint: meanThresholdValue ?? null,
        setpointMin: minThresholdValues[0]?.value ?? null,
        setpointMax: maxThresholdValues[0]?.value ?? null,
      },
      textAlign: 'center',
    },
  );

  // Add series
  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Value',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
      tooltip: showTooltip ? tooltip : null,
    }),
  );

  if (showSD) {
    const stdDevSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Standard Deviation',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'stdDevValue',
        valueXField: 'date',
        stroke: '#E3E8EF',
      }),
    );

    const stdDevPosSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: '+SD',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'stdDevPosValue',
        valueXField: 'date',
        stroke: '#3A7E73',
      }),
    );

    const stdDevNegSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: '-SD',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'stdDevNegValue',
        valueXField: 'date',
        stroke: '#4294FF',
      }),
    );

    stdDevSeries.data.setAll(data);
    stdDevPosSeries.data.setAll(data);
    stdDevNegSeries.data.setAll(data);
  }

  // Process data to create vertical drops or rises
  const processedMinThresholdValues = processThresholdValues(minThresholdValues);
  const processedMaxThresholdValues = processThresholdValues(maxThresholdValues);
  // Add series
  const setMinPointSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Value',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
    }),
  );

  setMinPointSeries.data.setAll(processedMinThresholdValues);
  setMinPointSeries.set('stroke', am5.color('#F97066'));

  const setMaxPointSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Value',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
    }),
  );

  setMaxPointSeries.data.setAll(processedMaxThresholdValues);
  setMaxPointSeries.set('stroke', am5.color('#F97066'));
  // Set custom stroke color and thickness
  series.set('stroke', am5.color(chartColor));
  generateLineType(series, lineType, lineWeight);

  // Add data
  series.data.setAll(sampledData);

  buildCursor(chart, root, series, xAxis, yAxis, chartColor, callbacks, syncCursor);

  chart.get('cursor').events.on('zoomed', function () {
    const zoomLevel = chart.get('cursor').get('zoomX');
    const newMinGridDistance = calculateMinGridDistance(zoomLevel);
    xAxis.get('renderer').set('minGridDistance', newMinGridDistance);
  });

  function calculateMinGridDistance(zoomLevel: number) {
    // Implement your logic to calculate minGridDistance based on zoomLevel
    // For example:
    return Math.max(50, 100 / zoomLevel);
  }
};

export const generateMarkers = (markerType: string, markerColor: string, markerSize: number, root: am5.Root) => {
  markerSize = Number(markerSize);
  if (markerType === 'triangle') {
    return am5.Triangle.new(root, {
      fill: am5.color(markerColor),
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      width: 5 + markerSize,
      height: 5 + markerSize,
      centerX: am5.p50,
      centerY: am5.p50,
      rotation: 180,
    });
  } else if (markerType === 'inverseTriangle') {
    return am5.Triangle.new(root, {
      fill: am5.color(markerColor),
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      width: 5 + markerSize,
      height: 5 + markerSize,
      centerX: am5.p50,
      centerY: am5.p50,
      rotation: 0,
    });
  } else if (markerType === 'square') {
    return am5.Rectangle.new(root, {
      fill: am5.color(markerColor),
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      width: 5 + markerSize,
      height: 5 + markerSize,
      centerX: am5.p50,
      centerY: am5.p50,
    });
  } else if (markerType === 'circle') {
    return am5.Circle.new(root, {
      fill: am5.color(markerColor),
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      radius: 2 + markerSize,
      centerX: am5.p50,
      centerY: am5.p50,
    });
  } else if (markerType === 'diamond') {
    return am5.Graphics.new(root, {
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      fill: am5.color(markerColor),
      fillOpacity: 1,
      rotation: 45,
      centerX: am5.p50,
      centerY: am5.p50,
      draw: function (display) {
        display.drawRect(0, 0, 5 + markerSize, 5 + markerSize);
      },
    });
  } else if (markerType === 'croosmark') {
    return am5.Graphics.new(root, {
      stroke: am5.color(markerColor),
      strokeWidth: 2,
      fill: am5.color(markerColor),
      fillOpacity: 0.5,
      draw: function (display) {
        // Draw a cross shape
        const size = 5 + markerSize; // Size of the cross
        const halfSize = size / 2;

        // Diagonal line from top-left to bottom-right
        display.moveTo(-halfSize + 1, -halfSize + 1);
        display.lineTo(halfSize - 1, halfSize - 1);

        // Diagonal line from top-right to bottom-left
        display.moveTo(halfSize - 1, -halfSize + 1);
        display.lineTo(-halfSize + 1, halfSize - 1);
      },
    });
  } else if (markerType === 'asterick') {
    return am5.Graphics.new(root, {
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      fill: am5.color(markerColor),
      fillOpacity: 0.5,
      draw: function (display) {
        // Draw an "X" shape
        const size = 5 + markerSize; // Size of the "X"
        const halfSize = size / 2;

        // Vertical line
        display.moveTo(0, -halfSize);
        display.lineTo(0, halfSize);

        // Diagonal line from top-left to bottom-right
        display.moveTo(-halfSize + 1, -halfSize + 1);
        display.lineTo(halfSize - 1, halfSize - 1);

        // Diagonal line from top-right to bottom-left
        display.moveTo(halfSize - 1, -halfSize + 1);
        display.lineTo(-halfSize + 1, halfSize - 1);
      },
    });
  } else {
    return am5.Triangle.new(root, {
      fill: am5.color(markerColor),
      stroke: am5.color(markerColor),
      strokeWidth: 1,
      width: markerSize,
      height: markerSize,
      centerX: am5.p50,
      centerY: am5.p50,
      rotation: 180,
    });
  }
};
const generateScatterPlotTimeSeries = (seriesChartProps: ChartProps) => {
  const {
    chart,
    root,
    xAxis,
    yAxis,
    data,
    maxThresholdValues,
    minThresholdValues,
    callbacks,
    markerColor,
    markerType,
    markerSize,
    title,
    shortUnitOfMeasure,
    syncCursor,
    showTooltip,
  } = seriesChartProps;

  // Downsample scatter as well to avoid rendering excessive bullets
  const maxPoints = chooseMaxPointsForContainer(chart.root.dom, 'scatter');
  const sampledData = data.length > maxPoints ? lttb(data, maxPoints) : data;

  const meanThresholdValue = ((minThresholdValues[0]?.value ?? 0) + (maxThresholdValues[0]?.value ?? 0)) / 2;
  const tooltip = createTooltip(
    `${title} {valueY} ${shortUnitOfMeasure} {valueX.formatDate("MMM d, yyyy hh:mm a")}`,
    root,
    {
      backgroundColor: '#001023',
      backgroundOpacity: 0.6,
      strokeColor: '#4A5463',
      textColor: '#FFFFFF',
      fontSize: 14,
      verticalPosition: 'top',
      thresholds: {
        setpoint: meanThresholdValue ?? null,
        setpointMin: minThresholdValues[0]?.value ?? null,
        setpointMax: maxThresholdValues[0]?.value ?? null,
      },
      textAlign: 'center',
    },
  );
  // Add scatter series
  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Scatter Plot',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
      tooltip: showTooltip ? tooltip : null,
    }),
  );

  // Process data to create vertical drops or rises
  const processedMinThresholdValues = processThresholdValues(minThresholdValues);
  const processedMaxThresholdValues = processThresholdValues(maxThresholdValues);
  // Add series
  const setMinPointSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Value',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
    }),
  );

  setMinPointSeries.data.setAll(processedMinThresholdValues);
  setMinPointSeries.set('stroke', am5.color('#F97066'));

  const setMaxPointSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Value',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
    }),
  );

  setMaxPointSeries.data.setAll(processedMaxThresholdValues);
  setMaxPointSeries.set('stroke', am5.color('#F97066'));

  series.strokes.template.set('strokeOpacity', 0);

  series.bullets.push(function () {
    const graphics = generateMarkers(markerType, markerColor, markerSize, root);
    return am5.Bullet.new(root, {
      sprite: graphics,
    });
  });

  // Add data
  series.data.setAll(sampledData);

  buildCursor(chart, root, series, xAxis, yAxis, markerColor, callbacks, syncCursor);
};

export const yAxisInfoArray: { title: string; min: number | undefined; max: number | undefined; key: string }[] = [];

export const yAxisOverrides: Record<string, { min: number; max: number }> = {};

export const createTimeSeriesChart = (
  container: HTMLElement,
  data: DataPoint[],
  maxThresholdValues: DataPoint[],
  minThresholdValues: DataPoint[],
  callbacks: ChartCallbacks,
  chartConfig: ChartConfig,
  title: string,
  shortUnitOfMeasure: string,
  addAmChartRef: (index: number, chart: XYChart, yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>, id: string) => void, // <-- changed
  chartIndex: number,
  syncCursor: () => void,
  syncAxes: (chart: XYChart) => void,
  chartViewOptions: ChartViewOptions,
  downtimeData?: DowntimeEntry[],
  pumpCheckerEventsSlice?: HighlightEvent[],
  startDate?: Date,
  endDate?: Date,
) => {
  // Ensure any existing chart root on this container is disposed before creating a new one
  prepareChartContainer(container);
  // Create root element
  const root = am5.Root.new(container);
  const { chartColor, markerColor, markerType, lineType, lineWeight, markerSize } = chartConfig;
  // Set themes
  root.setThemes([am5themes_Dark.new(root)]);

  const { chartType, showTooltip, showSD } = chartViewOptions;

  // Create chart
  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      paddingLeft: 0,
      paddingRight: 30,
    }),
  );

  // Create axes
  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0.2,
      baseInterval: {
        timeUnit: 'second',
        count: 1,
      },
      renderer: am5xy.AxisRendererX.new(root, {
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
        minGridDistance: 80,
        minorGridEnabled: true,
        pan: 'zoom',
      }),
    }),
  );

  if (startDate && endDate) {
    xAxis.setAll({
      min: new Date(startDate).getTime(),
      max: new Date(endDate).getTime(),
      strictMinMax: true,
    });
  }

  const xAxisRenderer = xAxis.get('renderer');

  xAxisRenderer.grid.template.setAll({
    strokeOpacity: 0,
  });

  xAxisRenderer.labels.template.setAll({
    fontFamily: 'Mulish',
    fontSize: '12px',
    fontWeight: '400',
    paddingTop: 10,
    minPosition: 0,
    maxPosition: 1,
  });

  xAxisRenderer.ticks.template.setAll({
    stroke: am5core.color('#FFFFFF'),
    strokeOpacity: 1,
    visible: true,
    minPosition: 0,
    maxPosition: 1,
  });

  xAxisRenderer.labels.template.setAll({
    location: 0,
  });

  const override = yAxisOverrides[title];

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      min: override?.min,
      max: override?.max,
      strictMinMax: !!override,
      maxDeviation: 0.2,
      renderer: am5xy.AxisRendererY.new(root, {
        pan: 'zoom',
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
      }),
    }),
  );

  // Add unit of measure labels to y-axis
  yAxis.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      text: shortUnitOfMeasure,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 12,
      fontFamily: 'Mulish',
      fontWeight: '400',
      textAlign: 'center',
    }),
  );

  yAxis.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      text: title,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 16,
      fontFamily: 'Mulish',
      fontWeight: '400',
      marginRight: -10,
      textAlign: 'center',
      maxHeight: 180,
      oversizedBehavior: 'wrap',
    }),
  );

  // Customize y-axis grid lines
  const yRenderer = yAxis.get('renderer');
  yRenderer.grid.template.setAll({
    strokeOpacity: 0,
  });

  // Customize y-axis labels to add unit suffix
  customizeAxisLabels(yRenderer, 12);

  const seriesChartProps: ChartProps = {
    chart,
    root,
    xAxis,
    yAxis,
    data,
    maxThresholdValues,
    minThresholdValues,
    callbacks,
    chartColor,
    markerColor,
    lineType,
    lineWeight,
    markerSize,
    markerType,
    title,
    shortUnitOfMeasure,
    syncCursor,
    showTooltip,
    showSD,
  };
  if (chartType === 'line') {
    generateTimeSeries(seriesChartProps);
  } else if (chartType === 'scatter') {
    generateScatterPlotTimeSeries(seriesChartProps);
  }

  const meanThresholdValue = ((minThresholdValues[0]?.value ?? 0) + (maxThresholdValues[0]?.value ?? 0)) / 2;

  renderDowntimeAndHighlightEvents(
    root,
    xAxis,
    chartViewOptions,
    downtimeData,
    showTooltip,
    pumpCheckerEventsSlice,
    true,
    {
      setpoint: meanThresholdValue ?? null,
      setpointMin: minThresholdValues[0]?.value ?? null,
      setpointMax: maxThresholdValues[0]?.value ?? null,
    },
  );

  xAxis.on('start', function () {
    if (!xAxis._skipSync) {
      syncAxes(chart);
    }
  });
  xAxis.on('end', function () {
    if (!xAxis._skipSync) {
      syncAxes(chart);
    }
  });

  // Add additional zoom synchronization events
  xAxis.onPrivate('selectionMin', () => {
    if (!xAxis._skipSync) {
      syncAxes(chart);
    }
  });
  xAxis.onPrivate('selectionMax', () => {
    if (!xAxis._skipSync) {
      syncAxes(chart);
    }
  });

  // Handle zoom changes via cursor events
  const cursor = chart.get('cursor');
  if (cursor) {
    cursor.events.on('selectended', () => {
      if (!xAxis._skipSync) {
        syncAxes(chart);
      }
    });
  }

  setTimeout(() => {
    const min = yAxis.getPrivate('min');
    const max = yAxis.getPrivate('max');
    const entry = {
      title,
      min,
      max,
      key: `${title}:${min}:${max}`,
    };
    // De-duplicate by title: update existing entry instead of pushing endlessly
    const idx = yAxisInfoArray.findIndex((item) => item.title === title);
    if (idx >= 0) {
      yAxisInfoArray[idx] = entry;
    } else {
      yAxisInfoArray.push(entry);
    }
  }, 0);

  addAmChartRef(chartIndex, chart, yAxis, title);
  return { root, yAxis, title };
};
